import { AnimatePresence, motion } from "framer-motion";
import { type JSX } from "react";

import { paletteIn, scrimFade } from "../lib/motion";
import { Button, Kbd } from "./primitives";
import { IconLock, IconShield, IconSparkle } from "./icons";

const ONBOARD_KEY = "voss-onboarded";
export const hasOnboarded = (): boolean => (typeof localStorage !== "undefined" ? localStorage.getItem(ONBOARD_KEY) === "true" : true);
export const markOnboarded = (): void => localStorage.setItem(ONBOARD_KEY, "true");

const POINTS = [
  { icon: <IconSparkle width={16} height={16} />, title: "Workspaces, not just chats", body: "Personal, Work and more — each with its own private memory and context." },
  { icon: <IconLock />, title: "Private by default", body: "Sensitive workspaces stay local. VOSS never silently sends them to the cloud." },
  { icon: <IconShield />, title: "Answers you can trust", body: "Every reply shows whether it was grounded in memory or generated — never faked." },
];

/** First-run welcome. Calm, one screen, dismissible — sets a flag so it only appears once. */
export function Onboarding({ open, onDismiss }: { open: boolean; onDismiss: () => void }): JSX.Element {
  return (
    <AnimatePresence>
      {open ? (
        <motion.div className="voss-scrim" variants={scrimFade} initial="hidden" animate="visible" exit="exit" style={{ display: "grid", placeItems: "center" }}>
          <motion.div className="voss-onboard" variants={paletteIn} initial="hidden" animate="visible" exit="exit" role="dialog" aria-modal="true" aria-label="Welcome to VOSS">
            <div className="voss-landing__mark" aria-hidden style={{ width: 56, height: 56, borderRadius: 16 }} />
            <h2 className="voss-onboard__title">Welcome to VOSS</h2>
            <p className="voss-onboard__sub">Your personal intelligence operating system. A few things that make it different:</p>
            <div className="voss-onboard__points">
              {POINTS.map((p) => (
                <div key={p.title} className="voss-onboard__point">
                  <span className="voss-ws__glyph" aria-hidden>
                    {p.icon}
                  </span>
                  <div>
                    <div className="voss-field__label">{p.title}</div>
                    <div className="voss-field__desc">{p.body}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="voss-onboard__foot">
              <span className="voss-field__desc">
                Tip: press <Kbd>⌘K</Kbd> anytime
              </span>
              <Button variant="primary" onClick={onDismiss}>
                Get started
              </Button>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
